import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import {
    ClosedownJobDetail,
    ClosedownOption,
    ClosedownPayload,
    ClosedownStatus,
    ClosedownUploadRequest,
    RootCause,
    StatusAffirmation
} from '@app/models/closedown-detail.model';
import { catchError, Observable, tap } from 'rxjs';
import { DashboardSummary, Job, OpenJobsSummary, PickupJob, UnallocateJob } from 'src/app/models/job.model';
import { StorageKey } from 'src/app/shared/enums';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base/base.service';

@Injectable({
    providedIn: 'root'
})
export class JobService extends BaseService {
    readonly KEY = StorageKey[StorageKey.JOB];
    readonly BASE_URL = `${environment.gatewayAPI_V1}/Jobs/`;
    //readonly BASE_URL = `https://localhost:44325/api/v1/`;

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getJobExistsByCompanyRefNumberNEmail(emailAddress: string, companyRefNumbers: string[]): Observable<string> {
        const url = `${this.BASE_URL}Jobs/getJobExistsByCompanyRefNumberNEmail`;
        let params = new HttpParams();

        params = params.append('userEmail', emailAddress);

        companyRefNumbers.forEach(refNumber => {
            params = params.append('companyRefNumbers', refNumber);
        })

        return this.httpClient.get<string>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsBySelectedReasons(reasons: string[], siteId: string, calloutReferenceNumbers: string[]): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsBySelectedReasons_v2`;
        let params = new HttpParams();

        reasons.forEach(refNumber => {
            params = params.append('reasons', refNumber);
        });

        calloutReferenceNumbers.forEach(crefNumber => {
            params = params.append('calloutReferenceNumbers', crefNumber);
        });

        params = params.append('siteId', siteId);
        
        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumberNEmail(companyRefNumbers: string[], siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumberNEmail`;
        let params = new HttpParams();

        companyRefNumbers.forEach(refNumber => {
            params = params.append('companyRefNumbers', refNumber);
        })

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumber(siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumber`;
        let params = new HttpParams();

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getResourceAdminJobsBySiteId(siteId: string): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetResourceAdminJobsBySiteId`;
        const params = new HttpParams().set('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByCompanyRefNumberWithSkipHeader(siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/getJobsByCompanyRefNumber`;
        let params = new HttpParams();

        if (siteId != null && siteId != undefined)
            params = params.append('siteId', siteId);

        // Add skip header to hide loading spinner
        return this.httpClient.get<Job[]>(url, { params: params, headers:{skip:"true"} }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getDashboardSummary(): Observable<DashboardSummary> {
        const url = `${this.BASE_URL}Jobs/GetDashboardSummary`;

        return this.httpClient.get<DashboardSummary>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getOpenJobsSummary(): Observable<OpenJobsSummary[]> {
        const url = `${this.BASE_URL}Jobs/GetOpenJobsSummary`;

        return this.httpClient.get<OpenJobsSummary[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJob(jobId: string): Observable<ClosedownJobDetail> {
        const url = `${this.BASE_URL}Jobs/GetResourceAdminJobById`;
        const params = new HttpParams().set('jobId', jobId);

        return this.httpClient.get<ClosedownJobDetail>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getRootCauses(
        brandId: string,
        disciplineReferenceId: string,
        faultServiceReferenceId: string,
        incidentTypeReferenceId?: string
    ): Observable<RootCause[]> {
        const url = `${this.BASE_URL}RootCauses/GetAvailableRootCauses`;
        let params = new HttpParams()
            .set('brandId', brandId)
            .set('disciplineReferenceId', disciplineReferenceId)
            .set('faultServiceReferenceId', faultServiceReferenceId);

        if (incidentTypeReferenceId) {
            params = params.set('incidentTypeReferenceId', incidentTypeReferenceId);
        }

        return this.httpClient.get<RootCause[]>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getStatuses(): Observable<ClosedownStatus[]> {
        const url = `${this.BASE_URL}ClosedownStatuses/GetClosedownStatuses`;

        return this.httpClient.get<ClosedownStatus[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getAffirmations(statusId: string): Observable<StatusAffirmation[]> {
        const url = `${this.BASE_URL}StatusAffirmations/GetStatusAffirmations`;
        const params = new HttpParams().set('closedownStatusId', statusId);

        return this.httpClient.get<StatusAffirmation[]>(url, { params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getGasTypes(): Observable<ClosedownOption[]> {
        const url = `${this.BASE_URL}/gastypes`;

        return this.httpClient.get<ClosedownOption[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getGasLeakTypes(disciplineId: number): Observable<ClosedownOption[]> {
        const url = `${this.BASE_URL}/gasleaktypes/${disciplineId}`;

        return this.httpClient.get<ClosedownOption[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getPhotoReasons(): Observable<string[]> {
        const url = `${this.BASE_URL}/FileStorages/GetFileStorageJobPhotoReasons`;

        return this.httpClient.get<string[]>(url).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public uploadFiles(request: ClosedownUploadRequest): Observable<void> {
        const url = `${this.BASE_URL}/FileStorages/UploadFiles`;

        return this.httpClient.post<void>(url, request).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public saveClosedown(jobId: string, payload: ClosedownPayload): Observable<void> {
        const url = `${this.BASE_URL}/jobs/${jobId}/closedown`;

        return this.httpClient.post<void>(url, payload).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );
    }

    public getJobsByAllocatedEmail(allocatedEmail: string, siteId: string | undefined): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsByAllocatedEmail`;
        let params = new HttpParams();

        params = params.append('allocatedEmail', allocatedEmail);

        if (siteId && siteId != undefined)
            params = params.append('siteId', siteId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );

    }

    public getJobsByJobNSubContractorId(jobId: string, subContractorId: string): Observable<Job[]> {
        const url = `${this.BASE_URL}Jobs/GetJobsByJobNSubContractorId`;
        let params = new HttpParams();

        params = params.append('jobId', jobId);
        params = params.append('subContractorId', subContractorId);

        return this.httpClient.get<Job[]>(url, { params: params }).pipe(
            tap(_ => _),
            catchError(this.handleError)
        );

    }

    public pickupJob(pickupJob: PickupJob): Observable<boolean> {
        const url = `${this.BASE_URL}Jobs/PickupJob`;
        //const url = `https://localhost:7062/v1/gateway/Jobs/Jobs/PickupJob`;

        return this.httpClient.post<boolean>(url, pickupJob).pipe(
            tap(_ => _),
            catchError(this.handleError));
    }

    public unallocateJob(unallocatedJob: UnallocateJob): Observable<boolean> {
        const url = `${this.BASE_URL}Jobs/UnallocateJob`;
        //const url = `https://localhost:7062/v1/gateway/Jobs/Jobs/UnallocateJob`;
        let params = new HttpParams();

        return this.httpClient.post<boolean>(url, unallocatedJob).pipe(
            tap(_ => _),
            catchError(this.handleError));
    }

    async getSignedInJob(): Promise<Job> {
        const item = await Preferences.get({ key: this.KEY });
        var obj;
        if (item.value != null)
            obj = JSON.parse(item.value);

        return obj;
    }

    async setSignedInJob(job: Job) {
        await Preferences.set({
            key: this.KEY,
            value: JSON.stringify(job),
        });
    }

    async removeSignedInJob() {
        await Preferences.remove({ key: StorageKey[this.KEY] });
    }
}